import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Alert,
  Button,
  CircularProgress,
  Divider,
} from '@mui/material';
import { accountApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { AlertInfo } from '../types';

interface AccountSettingsProps {
  showAlert: (message: string, severity: AlertInfo['severity']) => void;
}

interface AccountData {
  id: string;
  name: string;
  blockRoleBasedEmails: boolean;
  createdAt: string;
  updatedAt: string;
}

const AccountSettings: React.FC<AccountSettingsProps> = ({ showAlert }) => {
  const [account, setAccount] = useState<AccountData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [blockRoleBasedEmails, setBlockRoleBasedEmails] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    loadAccountSettings();
  }, []);

  const loadAccountSettings = async () => {
    try {
      setLoading(true);
      const response = await accountApi.getById(user?.accountId || '');
      setAccount(response);
      setBlockRoleBasedEmails(response.blockRoleBasedEmails || false);
    } catch (error: any) {
      console.error('Failed to load account settings:', error);
      showAlert('Failed to load account settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!account) return;

    try {
      setSaving(true);
      await accountApi.update(account.id, {
        blockRoleBasedEmails
      });
      
      showAlert('Account settings saved successfully', 'success');
      await loadAccountSettings(); // Reload to get updated data
    } catch (error: any) {
      console.error('Failed to save account settings:', error);
      showAlert('Failed to save account settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!account) {
    return (
      <Alert severity="error">
        Failed to load account information
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Account Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure your account preferences and email validation settings
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Account Information
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Account Name
            </Typography>
            <Typography variant="body1">
              {account.name}
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Account ID
            </Typography>
            <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
              {account.id}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body1">
              {new Date(account.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Email Validation Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure how email addresses are validated before sending campaigns
          </Typography>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={blockRoleBasedEmails}
                  onChange={(e) => setBlockRoleBasedEmails(e.target.checked)}
                  disabled={saving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    Block Role-Based Email Addresses
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Prevent sending emails to role-based addresses like admin@, info@, sales@, etc.
                    When disabled, these emails will be allowed but marked as role-based in validation results.
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Email validation always checks for syntax, disposable domains, 
              MX records, and SMTP mailbox verification. This setting only controls whether 
              role-based addresses (like admin@, info@, support@) are considered invalid.
            </Typography>
          </Alert>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSaveSettings}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : null}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AccountSettings;
